/**
 * Truck Icon Atlas for DeckGL IconLayer
 *
 * This module provides icon atlas configuration for rendering truck markers
 * using DeckGL's GPU-accelerated IconLayer instead of DOM-based Google Maps markers.
 *
 * The icon atlas is a single image containing all icon variants, which DeckGL
 * can efficiently render using WebGL texture sampling.
 */

import { DROPOFF_PHASE_COLOR, PICKUP_PHASE_COLOR } from './loadPhase';

/**
 * Icon dimensions in pixels.
 * These values are used for both the atlas layout and the icon mapping.
 * 40x40 provides padding around the path for effects like drop shadows
 * to render without bleeding into adjacent cells.
 */
const ICON_SIZE = 40;

/**
 * SVG path for the truck arrow icon.
 * This arrow points upward (north) by default; rotation is applied via DeckGL's getAngle accessor.
 * Path is designed for a 28x30 viewBox, scaled to fit within ICON_SIZE.
 */
const TRUCK_ARROW_PATH = 'M14 0 28 30 14 24 0 30 14 0';

/**
 * SVG path for a circular marker (used for moving sites).
 * Circle path using arc commands for smooth rendering.
 */
const CIRCLE_PATH = 'M16,4 A12,12 0 1,0 16,28 A12,12 0 1,0 16,4';

/**
 * Color definitions for truck icon states.
 * These colors match the existing TreadMarker.tsx implementation. The pickup and
 * dropoff colors are shared with the site icons so a truck and the site it is
 * headed toward read as the same color (see loadPhase.ts).
 */
const TRUCK_ICON_COLORS = {
  default: '#006F12', // Green - default truck color
  active: '#163786', // Blue - selected/active truck
  idle: '#FFA000', // Amber - idle truck
  movingSite: '#000000', // Black - moving site marker
  pickup: PICKUP_PHASE_COLOR, // Blue - truck in the pickup phase (matches pickup site)
  dropoff: DROPOFF_PHASE_COLOR, // Navy - truck in the dropoff phase (matches dropoff site)
  stroke: '#FFFFFF', // White - default icon stroke/outline
} as const;

/**
 * Creates an SVG string for the truck arrow icon.
 *
 * @param fillColor - The fill color for the arrow
 * @param strokeColor - The stroke color for the arrow outline
 * @returns SVG string that can be used in a data URL
 */
const createTruckArrowSvg = (
  fillColor: string,
  strokeColor: string = TRUCK_ICON_COLORS.stroke,
  strokeWidth: number = 3,
): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 ${ICON_SIZE} ${ICON_SIZE}">
    <g transform="translate(2.5, 1.25) scale(1.1875)">
      <path d="${TRUCK_ARROW_PATH}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>
    </g>
  </svg>`;
};

/**
 * Creates an SVG string for the moving site circle icon.
 *
 * @returns SVG string for the moving site marker
 */
const createMovingSiteSvg = (): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 ${ICON_SIZE} ${ICON_SIZE}">
    <g transform="scale(1.25)">
      <path d="${CIRCLE_PATH}" fill="${TRUCK_ICON_COLORS.movingSite}" stroke="${TRUCK_ICON_COLORS.stroke}" stroke-width="2"/>
    </g>
  </svg>`;
};

/**
 * Icon types available in the truck icon atlas.
 */
export type TruckIconType =
  | 'truck'
  | 'truck_active'
  | 'truck_idle'
  | 'truck_pickup'
  | 'truck_dropoff'
  | 'moving_site';

/**
 * Icon mapping configuration for DeckGL IconLayer.
 * Each entry defines the position and dimensions of an icon within the atlas.
 *
 * @property x - X position of the icon in the atlas (pixels)
 * @property y - Y position of the icon in the atlas (pixels)
 * @property width - Width of the icon (pixels)
 * @property height - Height of the icon (pixels)
 * @property anchorX - X anchor point for positioning (pixels from left)
 * @property anchorY - Y anchor point for positioning (pixels from top)
 * @property mask - If true, the icon color can be overridden via getColor
 */
interface IconMappingEntry {
  x: number;
  y: number;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask: boolean;
}

/**
 * Icon mapping for the truck icon atlas.
 * Maps icon names to their positions and properties in the atlas image.
 */
export const TRUCK_ICON_MAPPING: Record<TruckIconType, IconMappingEntry> = {
  truck: {
    x: 0,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false, // Don't mask - use SVG colors directly
  },
  truck_active: {
    x: ICON_SIZE,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  truck_idle: {
    x: ICON_SIZE * 2,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  moving_site: {
    x: ICON_SIZE * 3,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  truck_pickup: {
    x: ICON_SIZE * 4,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  truck_dropoff: {
    x: ICON_SIZE * 5,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
};

/**
 * Total width of the icon atlas (contains 6 icons side by side:
 * truck, truck_active, truck_idle, moving_site, truck_pickup, truck_dropoff).
 */
const ATLAS_WIDTH = ICON_SIZE * 6;

/**
 * Total height of the icon atlas (single row of icons).
 */
const ATLAS_HEIGHT = ICON_SIZE;

/**
 * Creates the combined SVG atlas containing all truck icon variants.
 * Icons are arranged horizontally: [truck, truck_active, moving_site]
 *
 * @returns SVG string containing all icons
 */
const createIconAtlasSvg = (): string => {
  const truckSvgContent = createTruckArrowSvg(TRUCK_ICON_COLORS.default);
  const truckActiveSvgContent = createTruckArrowSvg(TRUCK_ICON_COLORS.active);
  const truckIdleSvgContent = createTruckArrowSvg(TRUCK_ICON_COLORS.idle);
  const truckPickupSvgContent = createTruckArrowSvg(TRUCK_ICON_COLORS.pickup);
  const truckDropoffSvgContent = createTruckArrowSvg(TRUCK_ICON_COLORS.dropoff);
  const movingSiteSvgContent = createMovingSiteSvg();

  // Extract inner content from each SVG (remove outer svg tags)
  const extractSvgContent = (svg: string): string => {
    const match = svg.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
    return match ? match[1] : '';
  };

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ATLAS_WIDTH}" height="${ATLAS_HEIGHT}" viewBox="0 0 ${ATLAS_WIDTH} ${ATLAS_HEIGHT}">
    <defs>
      <filter id="idle-shadow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="#000000" flood-opacity="0.7"/>
      </filter>
    </defs>
    <g transform="translate(0, 0)">${extractSvgContent(truckSvgContent)}</g>
    <g transform="translate(${ICON_SIZE}, 0)">${extractSvgContent(truckActiveSvgContent)}</g>
    <g transform="translate(${ICON_SIZE * 2}, 0)" filter="url(#idle-shadow)">${extractSvgContent(truckIdleSvgContent)}</g>
    <g transform="translate(${ICON_SIZE * 3}, 0)">${extractSvgContent(movingSiteSvgContent)}</g>
    <g transform="translate(${ICON_SIZE * 4}, 0)">${extractSvgContent(truckPickupSvgContent)}</g>
    <g transform="translate(${ICON_SIZE * 5}, 0)">${extractSvgContent(truckDropoffSvgContent)}</g>
  </svg>`;
};

/**
 * The truck icon atlas as a data URL.
 * This URL can be passed directly to DeckGL's IconLayer iconAtlas prop.
 *
 * Using a data URL allows the atlas to be embedded directly without
 * requiring a separate HTTP request to load an image file.
 */
export const TRUCK_ICON_ATLAS: string =
  'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(createIconAtlasSvg());

/**
 * Default icon size for rendering truck markers.
 * This can be adjusted based on zoom level or user preferences.
 */
export const DEFAULT_TRUCK_ICON_SIZE = 24;

/**
 * Icon size when a truck is in active/selected state.
 * Slightly larger to provide visual feedback.
 */
export const ACTIVE_TRUCK_ICON_SIZE = 28;
